import React, { useEffect, useState } from 'react';
import { Shield, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link, useLocation } from 'react-router-dom';
import { a11yLabel } from '../utils/a11yLabels';

const STORAGE_KEY = 'shs_cookie_consent_v1';
const POLICY_VERSION = 'v2-2026-02';
const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'shs_token';

// Map UI choice → GDPR purposes[] (Art. 7 — record granular purpose)
const purposesFor = (status) => {
  if (status === 'accepted_all') return ['essential', 'analytics', 'marketing'];
  // Rejected & dismissed both fall back to essential-only
  return ['essential'];
};
const consentStatusFor = (status) =>
  status === 'accepted_all' ? 'granted' : 'withdrawn';

/**
 * GDPR-friendly cookie / consent notice banner.
 * Persists in localStorage. Three states stored:
 *   { status: 'accepted_all' | 'rejected_non_essential' | 'accepted_essential_only',
 *     timestamp, version: 1 }
 *
 * Mounted globally in App.js. Does NOT block initial render; appears after
 * a 500ms delay to avoid layout shift on first paint.
 */
const CookieNoticeBanner = () => {
  const { language } = useLanguage();
  const isEs = language === 'es';
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (!existing) {
        const t = setTimeout(() => setVisible(true), 500);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);

  const persist = (status) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        status, timestamp: new Date().toISOString(), version: 1,
      }));
    } catch { /* ignore */ }
    setVisible(false);
    // Inform any downstream consumers (analytics gating, etc.)
    window.dispatchEvent(new CustomEvent('shs:consent-changed', { detail: { status } }));

    // Phase 4 Sprint 2 — server-side consent records for ALL visitors.
    // Authenticated users → /api/gdpr/consent (auth-gated, captures user_id/email)
    // Anonymous visitors  → /api/gdpr/consent/anonymous (hashed IP + session_id)
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (API_URL) {
        // Maintain a stable opaque session id for anonymous correlation
        let anonSessionId = localStorage.getItem('shs_anon_session_id');
        if (!anonSessionId) {
          anonSessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          try { localStorage.setItem('shs_anon_session_id', anonSessionId); } catch { /* ignore */ }
        }
        const payload = {
          policy_version: POLICY_VERSION,
          locale: isEs ? 'es' : 'en',
          purposes: purposesFor(status),
          status: consentStatusFor(status),
          notes: `cookie-banner:${status}`,
          ...(token ? {} : { session_id: anonSessionId }),
        };
        const url = token
          ? `${API_URL}/api/gdpr/consent`
          : `${API_URL}/api/gdpr/consent/anonymous`;
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        fetch(url, { method: 'POST', headers, body: JSON.stringify(payload), keepalive: true })
          .catch(() => { /* best-effort; never block UX */ });
      }
    } catch { /* never throw from consent UI */ }
  };

  if (!visible || pathname.startsWith('/admin')) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[70] pointer-events-none"
      role="dialog"
      aria-label={isEs ? 'Aviso de cookies' : 'Cookie notice'}
      data-testid="cookie-banner"
    >
      <div className="container mx-auto px-2 pb-2 sm:pb-3">
        <div className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-slate-200 p-4 sm:p-5 max-w-3xl mx-auto">
          <div className="flex items-start gap-3">
            <div className="hidden sm:flex h-9 w-9 rounded-lg bg-indigo-50 items-center justify-center flex-shrink-0">
              <Shield className="h-4 w-4 text-indigo-600" />
            </div>
            <div className="flex-1 text-sm text-slate-700 leading-relaxed">
              <p>
                {isEs
                  ? 'Usamos cookies esenciales para que el sitio funcione y, con su permiso, cookies analíticas para mejorar la experiencia educativa.'
                  : 'We use essential cookies to make the site work and, with your permission, analytics cookies to improve the learning experience.'}
                {' '}
                <Link to="/legal/privacy" className="underline text-indigo-700 hover:text-indigo-900">
                  {isEs ? 'Ver política de privacidad' : 'View privacy policy'}
                </Link>
              </p>
            </div>
            <button
              onClick={() => persist('rejected_non_essential')}
              className="text-slate-400 hover:text-slate-600 flex-shrink-0 sm:hidden"
              data-testid="cookie-banner-dismiss"
              aria-label={a11yLabel("Dismiss")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 justify-end">
            <button
              onClick={() => persist('rejected_non_essential')}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100"
              data-testid="cookie-reject"
            >
              {isEs ? 'Solo esenciales' : 'Essential only'}
            </button>
            <button
              onClick={() => persist('accepted_all')}
              className="px-4 py-1.5 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800"
              data-testid="cookie-accept"
            >
              {isEs ? 'Aceptar todo' : 'Accept all'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieNoticeBanner;
